import { useState } from "react";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Pencil, Ban, KeyRound, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_LANGS = ["en", "es", "fr", "ht"];
const langBadgeColors = {
  en: "bg-[#002FA7]/10 text-[#002FA7]",
  es: "bg-[#0A0A0A]/10 text-[#0A0A0A]",
  fr: "bg-[#00C853]/10 text-[#00C853]",
  ht: "bg-[#FFB800]/10 text-[#FFB800]",
};

export default function UsersTab({ t, users, projects, reload, currentUser }) {
  const isSuper = currentUser?.role === "super-admin";
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "", email: "", password: "", role: "operator", languages: ["en"], assigned_projects: [],
  });
  const [editUser, setEditUser] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");

  const toggleListItem = (list, item) =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      return toast.error("Name, email, password required");
    }
    if (newUser.password.length < 8) return toast.error("Password must be ≥ 8 chars");
    try {
      await ax.post(`${API}/auth/users`, newUser);
      toast.success("User created");
      setNewUser({ name: "", email: "", password: "", role: "operator", languages: ["en"], assigned_projects: [] });
      setCreateOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const saveEdit = async () => {
    try {
      const payload = {
        name: editUser.name,
        languages: editUser.languages,
        assigned_projects: editUser.assigned_projects || [],
      };
      if (isSuper) payload.role = editUser.role;
      await ax.put(`${API}/auth/users/${editUser.user_id}`, payload);
      toast.success("User updated");
      setEditUser(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deactivateUser = async (u) => {
    if (!window.confirm(`Deactivate ${u.name}? History will be preserved.`)) return;
    try {
      await ax.delete(`${API}/auth/users/${u.user_id}`);
      toast.success("User deactivated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reactivateUser = async (u) => {
    try {
      await ax.post(`${API}/auth/users/${u.user_id}/reactivate`);
      toast.success("User reactivated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resetPassword = async () => {
    if (resetPwd.length < 8) return toast.error("Password must be ≥ 8 chars");
    try {
      await ax.post(`${API}/auth/users/${resetTarget.user_id}/reset-password`, { password: resetPwd });
      toast.success("Password reset");
      setResetTarget(null);
      setResetPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">{t("nav.users")}</h3>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white h-9 text-xs font-bold tracking-[0.1em] uppercase" data-testid="create-user-btn">
              <UserPlus size={14} className="mr-1" /> New User
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-lg">
            <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input className="rounded-none" placeholder="Full Name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" />
              <Input className="rounded-none" placeholder="Email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" />
              <Input className="rounded-none" placeholder="Password (≥ 8 chars)" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password" />
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="rounded-none" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  {isSuper && <SelectItem value="admin">Admin</SelectItem>}
                  {isSuper && <SelectItem value="super-admin">Super Admin</SelectItem>}
                </SelectContent>
              </Select>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1 block">Languages</label>
                <div className="flex gap-2 flex-wrap">
                  {ALL_LANGS.map((lang) => (
                    <button key={lang} type="button"
                      onClick={() => setNewUser({ ...newUser, languages: toggleListItem(newUser.languages, lang) })}
                      className={`px-2 py-1 text-[10px] font-bold uppercase border ${newUser.languages.includes(lang) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#4B5563] border-[#E5E7EB]"}`}
                      data-testid={`new-user-lang-${lang}`}
                    >{lang}</button>
                  ))}
                </div>
              </div>
              {newUser.role === "operator" && (
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1 block">Assigned Projects</label>
                  <div className="space-y-1 max-h-32 overflow-y-auto border border-[#E5E7EB] p-2">
                    {projects.map((p) => (
                      <label key={p.project_id} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={newUser.assigned_projects.includes(p.project_id)}
                          onChange={() => setNewUser({ ...newUser, assigned_projects: toggleListItem(newUser.assigned_projects, p.project_id) })}
                          data-testid={`new-user-proj-${p.project_id}`}
                        /> {p.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase" onClick={createUser} data-testid="new-user-submit">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#E5E7EB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-b-2 border-[#0A0A0A]/80">
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("common.name")}</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("common.email")}</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("common.role")}</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">Languages</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">Projects</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">Status</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const deactivated = u.status === "deactivated";
              return (
                <TableRow key={u.user_id} className={`border-b border-[#E5E7EB] ${deactivated ? "opacity-50" : ""}`} data-testid={`user-row-${u.user_id}`}>
                  <TableCell className="py-3 text-sm font-medium">{u.name}</TableCell>
                  <TableCell className="py-3 text-sm text-[#4B5563]">{u.email}</TableCell>
                  <TableCell className="py-3">
                    <Badge variant="outline" className="rounded-none text-[10px] font-bold uppercase">{u.role}</Badge>
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="flex gap-0.5">
                      {(u.languages || []).map((l) => (
                        <span key={l} className={`px-1 text-[9px] font-bold uppercase ${langBadgeColors[l]}`}>{l}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-xs text-[#4B5563]">{(u.assigned_projects || []).length}</TableCell>
                  <TableCell className="py-3">
                    {deactivated ? (
                      <Badge className="rounded-none text-[9px] font-bold bg-[#FF2A2A]/10 text-[#FF2A2A]">DEACTIVATED</Badge>
                    ) : (
                      <Badge className="rounded-none text-[9px] font-bold bg-[#00C853]/10 text-[#00C853]">ACTIVE</Badge>
                    )}
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setEditUser({ ...u, languages: u.languages || ["en"], assigned_projects: u.assigned_projects || [] })} data-testid={`edit-user-${u.user_id}`}>
                        <Pencil size={12} />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setResetTarget(u)} data-testid={`reset-pwd-${u.user_id}`}>
                        <KeyRound size={12} />
                      </Button>
                      {isSuper && u.user_id !== currentUser?.user_id && (
                        deactivated ? (
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[#00C853]" onClick={() => reactivateUser(u)} data-testid={`reactivate-${u.user_id}`}>
                            <RotateCcw size={12} />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[#FF2A2A] hover:bg-[#FF2A2A]/10" onClick={() => deactivateUser(u)} data-testid={`deactivate-${u.user_id}`}>
                            <Ban size={12} />
                          </Button>
                        )
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Edit User */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="rounded-none max-w-lg" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <Input className="rounded-none" placeholder="Name" value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="edit-user-name" />
              <Input className="rounded-none bg-[#F8F9FA]" disabled value={editUser.email} />
              {isSuper && (
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger className="rounded-none" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {["super-admin", "admin", "operator", "client"].map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1 block">Languages</label>
                <div className="flex gap-2 flex-wrap">
                  {ALL_LANGS.map((lang) => (
                    <button key={lang} type="button"
                      onClick={() => setEditUser({ ...editUser, languages: toggleListItem(editUser.languages, lang) })}
                      className={`px-2 py-1 text-[10px] font-bold uppercase border ${editUser.languages.includes(lang) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#4B5563] border-[#E5E7EB]"}`}
                      data-testid={`edit-user-lang-${lang}`}
                    >{lang}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1 block">Assigned Projects</label>
                <div className="space-y-1 max-h-32 overflow-y-auto border border-[#E5E7EB] p-2">
                  {projects.map((p) => (
                    <label key={p.project_id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={editUser.assigned_projects.includes(p.project_id)}
                        onChange={() => setEditUser({ ...editUser, assigned_projects: toggleListItem(editUser.assigned_projects, p.project_id) })}
                        data-testid={`edit-user-proj-${p.project_id}`}
                      /> {p.name}
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold tracking-[0.1em] uppercase" onClick={saveEdit} data-testid="edit-user-save">Save</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && (setResetTarget(null), setResetPwd(""))}>
        <DialogContent className="rounded-none max-w-md" data-testid="reset-pwd-dialog">
          <DialogHeader><DialogTitle>Reset Password — {resetTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input className="rounded-none" type="password" placeholder="New password (≥ 8 chars)" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} data-testid="reset-pwd-input" />
            <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase" onClick={resetPassword} data-testid="reset-pwd-confirm">Reset Password</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
